<template>
	<div
		class="chat-box"
		ref="chatBoxEl"
		v-loading="_loading"
		element-loading-text="加载历史消息..."
		element-loading-background="rgba(255, 255, 255, 0.7)"
	>
	<!-- 图片预览弹窗（放在 chat-box 内，append-to-body 会把它挂到 body） -->
	<el-dialog
		:visible.sync="previewVisible"
		width="auto"
		:append-to-body="true"
		:show-close="true"
		custom-class="img-preview-dialog"
	>
		<img :src="previewSrc" class="preview-full-img" />
	</el-dialog>
		<div
			class="chat-item"
			:key="idx"
			v-for="(item, idx) of _chatDialogs"
			:class="item.chatRole !== 'assistant' ? 'mine' : ''"
		>
			<div class="header-img ml10" v-if="item.chatRole !== 'assistant'">
				<img v-if="session" :src="session.headImgUrl" alt="" />
			</div>

			<div class="header-img ml10" v-if="item.chatRole === 'assistant'">
				<img src="https://xiaoyi-1317629730.cos.ap-guangzhou.myqcloud.com/image/76ab72d19e2249a28f77e2d488de8c3f.jpg" alt="" />
			</div>

			<div class="content">
				<!-- 工具执行状态条 -->
				<div class="tool-status" v-if="item.isTooling && item.toolLabel">
					<span class="spin"></span>
					<span>{{ item.toolLabel }}</span>
				</div>

				<!-- 思考过程（深度思考模式） -->
				<div
					v-if="item.chatRole === 'assistant' && item.reasoning"
					class="thinking-block"
					:class="{ collapsed: item._thinkingCollapsed }"
				>
					<div class="thinking-header" @click="toggleThinking(item)">
						<span class="thinking-icon">
							<i class="el-icon-cpu" v-if="item.isStreaming && !item.chatContent"></i>
							<i class="el-icon-arrow-right" v-else :class="{ 'arrow-down': !item._thinkingCollapsed }"></i>
						</span>
						<span class="thinking-label">{{ item.isStreaming && !item.chatContent ? '思考中...' : '思考过程' }}</span>
					</div>
					<div class="thinking-content" v-show="!item._thinkingCollapsed">{{ item.reasoning }}</div>
				</div>

				<!-- 流式输出阶段：纯文本 + 光标 -->
				<div
					v-if="item.isStreaming && !item.isTooling"
					class="bubble-streaming"
					:class="{ cursor: item.isStreaming && !!item.chatContent }"
				>{{ item.chatContent }}</div>

				<!-- 完成后：用 v-md-preview 渲染 Markdown -->
				<v-md-preview
					v-if="!item.isStreaming && item.chatRole === 'assistant'"
					class="preview-box"
					:text="item.chatContent"
				></v-md-preview>

				<!-- 用户消息：文件卡片（在文本气泡上方） -->
				<div class="user-files" v-if="item.chatRole !== 'assistant' && item.files && item.files.length > 0">
					<div
						class="file-card"
						v-for="(f, fi) in item.files"
						:key="fi"
						:class="{ 'file-card--downloadable': !!f.url }"
						@click="f.url && downloadFile(f.url, f.name)"
					>
						<i class="el-icon-document file-card-icon"></i>
						<div class="file-card-info">
							<div class="file-card-name">{{ f.name }}</div>
							<div class="file-card-meta" v-if="f.meta">{{ f.meta }}{{ f.truncated ? '（已截取）' : '' }}</div>
						</div>
						<i class="el-icon-download file-card-dl" v-if="f.url"></i>
					</div>
				</div>

				<!-- 用户消息（纯文本）：有文件时用 displayContent（只含用户输入），无文件时用 chatContent -->
				<div
					v-if="item.chatRole !== 'assistant' && userDisplayText(item)"
					class="user-content"
				>{{ userDisplayText(item) }}</div>
				<div class="user-imgs" v-if="item.chatRole !== 'assistant' && item.imgUrls && item.imgUrls.length > 0">
					<img
						v-for="(img, i) in item.imgUrls"
						:key="i"
						:src="img"
						class="user-img-thumb"
						@click="previewImg(img)"
					/>
				</div>

				<!-- 商品卡片等结构化数据 -->
				<div
					class="goods-cards"
					v-if="item.structuredData && item.structuredData.type === 'goods_cards' && item.structuredData.cards && item.structuredData.cards.length > 0"
				>
					<div
						class="goods-card"
						v-for="(card, ci) in item.structuredData.cards"
						:key="ci"
					>
						<div class="name">{{ card.name }}</div>
						<div class="price"><span>¥</span>{{ card.salePrice }}</div>
						<div class="tag" v-if="card.path">{{ card.path }}</div>
					</div>
				</div>

				<!-- 思考中占位（首次 AI 气泡还未收到任何内容时） -->
				<div
					v-if="item.chatRole === 'assistant' && item.isStreaming && !item.chatContent && !item.reasoning && !item.isTooling"
					class="loading"
				>思考中 ...</div>
			</div>

			<div class="copy" v-if="item.finished">
				<a @click="copy(item.chatContent)">
					<i class="el-icon-document-copy"></i>
				</a>
			</div>
		</div>
	</div>
</template>

<script>
import 'highlight.js/styles/atom-one-dark.css';
import { getSession } from '@/utils/auth';

export default {
	name: 'chat-box-v2',

	props: {
		_chatDialogs: Array,
		_loading: { type: Boolean, default: false },
	},

	created() {
		this.session = getSession();
	},

	data() {
		return {
			session: null,
			previewVisible: false,
			previewSrc: '',
		};
	},

	watch: {
		_chatDialogs: {
			deep: true,
			handler(dialogs) {
				this.$nextTick(() => {
					this.scrollToBottom();
					this.scrollThinkingToBottom();
				});
				// 流式结束后自动折叠思考过程（有正式回答内容时）
				if (dialogs) {
					dialogs.forEach(item => {
						if (item.chatRole === 'assistant' && item.finished && item.reasoning && item.chatContent && item._thinkingCollapsed === undefined) {
							this.$set(item, '_thinkingCollapsed', true);
						}
					});
				}
			},
		},
	},

	methods: {
		// 用户消息气泡的显示文本
		// 有文件时只显示用户输入的文字（displayContent），没有文件时显示完整 chatContent
		userDisplayText(item) {
			if (item.files && item.files.length > 0) {
				return item.displayContent || '';
			}
			return item.chatContent || '';
		},

		scrollToBottom() {
			const el = this.$refs.chatBoxEl;
			if (el) {
				el.scrollTop = el.scrollHeight;
			}
		},

		scrollThinkingToBottom() {
			const box = this.$refs.chatBoxEl;
			if (!box) return;
			box.querySelectorAll('.thinking-content').forEach(el => {
				el.scrollTop = el.scrollHeight;
			});
		},

		copy(text) {
			if (!text) return;
			navigator.clipboard.writeText(text).then(() => {
				this.$message({ message: '已复制', type: 'success', duration: 1000 });
			});
		},

		toggleThinking(item) {
			this.$set(item, '_thinkingCollapsed', !item._thinkingCollapsed);
		},

		previewImg(src) {
			this.previewSrc = src;
			this.previewVisible = true;
		},

		downloadFile(url, name) {
			const a = document.createElement('a');
			a.href = url;
			a.download = name || 'file';
			a.target = '_blank';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		},
	},
};
</script>

<style lang="scss" scoped>
.chat-box {
	flex: 1;
	overflow-y: auto;
	padding: 16px 20px;

	.chat-item {
		display: flex;
		gap: 10px;
		margin-bottom: 16px;

		&.mine {
			flex-direction: row-reverse;

			.content {
				align-items: flex-end;
			}

			.user-files {
				display: flex;
				flex-direction: column;
				gap: 4px;
				align-items: flex-end;

				.file-card {
					display: flex;
					align-items: center;
					gap: 8px;
					padding: 7px 12px;
					background: #f0f4ff;
					border: 1px solid #d0dcff;
					border-radius: 8px;
					min-width: 160px;
					max-width: 260px;
					transition: border-color 0.15s, background 0.15s;

					.file-card-icon {
						color: #6126F5;
						font-size: 18px;
						flex-shrink: 0;
					}

					.file-card-info {
						min-width: 0;
						flex: 1;
						.file-card-name {
							font-size: 12px;
							font-weight: 500;
							color: #333;
							overflow: hidden;
							text-overflow: ellipsis;
							white-space: nowrap;
						}
						.file-card-meta {
							font-size: 11px;
							color: #888;
							margin-top: 2px;
						}
					}

					.file-card-dl {
						font-size: 14px;
						color: #aaa;
						flex-shrink: 0;
						opacity: 0;
						transition: opacity 0.15s, color 0.15s;
					}

					&.file-card--downloadable {
						cursor: pointer;

						&:hover {
							border-color: #6126F5;
							background: #e8eeff;

							.file-card-dl {
								opacity: 1;
								color: #6126F5;
							}
						}
					}
				}
			}

			.user-content {
				background: #1677ff;
				color: #fff;
				border-radius: 12px 4px 12px 12px;
				padding: 9px 13px;
				font-size: 14px;
				line-height: 1.65;
				word-break: break-word;
				white-space: pre-wrap;
			}

			.user-imgs {
				display: flex;
				flex-wrap: wrap;
				gap: 6px;
				justify-content: flex-end;
				max-width: 300px;

				.user-img-thumb {
					width: 120px;
					height: 120px;
					object-fit: cover;
					border-radius: 8px;
					border: 1px solid #e0e0e0;
					cursor: pointer;
					display: block;
					transition: opacity 0.15s;
					flex-shrink: 0;

					&:hover { opacity: 0.85; }
				}
			}
		}

		.header-img {
			width: 34px;
			height: 34px;
			flex-shrink: 0;

			img {
				width: 34px;
				height: 34px;
				border-radius: 50%;
				object-fit: cover;
			}
		}

		.content {
			display: flex;
			flex-direction: column;
			gap: 6px;
			max-width: 78%;

			.loading {
				font-size: 13px;
				color: #999;
				padding: 8px 0;
			}

			.thinking-block {
					background: #f9f0ff;
					border: 1px solid #d3adf7;
					border-radius: 8px;
					overflow: hidden;
					font-size: 13px;

					.thinking-header {
						display: flex;
						align-items: center;
						gap: 6px;
						padding: 7px 12px;
						cursor: pointer;
						user-select: none;
						color: #531dab;

						&:hover {
							background: #f0e6ff;
						}

						.thinking-label {
							font-weight: 500;
						}

						.arrow-down {
							transform: rotate(90deg);
							transition: transform 0.2s;
						}
					}

					.thinking-content {
						padding: 8px 12px 10px;
						color: #722ed1;
						line-height: 1.6;
						white-space: pre-wrap;
						word-break: break-word;
						border-top: 1px solid #d3adf7;
						max-height: 300px;
						overflow-y: auto;
					}
				}

			.bubble-streaming {
				background: #fff;
				border: 1px solid #e8e8e8;
				border-radius: 4px 12px 12px 12px;
				padding: 9px 13px;
				font-size: 14px;
				line-height: 1.65;
				word-break: break-word;
				white-space: pre-wrap;

				&.cursor::after {
					content: '▋';
					animation: blink 1s step-end infinite;
					color: #1677ff;
					font-size: 11px;
				}
			}

			.tool-status {
				display: flex;
				align-items: center;
				gap: 8px;
				padding: 7px 13px;
				background: #f6ffed;
				border: 1px solid #b7eb8f;
				border-radius: 8px;
				font-size: 13px;
				color: #389e0d;

				.spin {
					display: inline-block;
					width: 13px;
					height: 13px;
					border: 2px solid #b7eb8f;
					border-top-color: #52c41a;
					border-radius: 50%;
					animation: spin 0.8s linear infinite;
					flex-shrink: 0;
				}
			}

			.goods-cards {
				display: flex;
				gap: 10px;
				flex-wrap: wrap;

				.goods-card {
					background: #fff;
					border: 1px solid #e8e8e8;
					border-radius: 10px;
					padding: 10px;
					width: 150px;
					cursor: pointer;
					transition: box-shadow 0.2s, transform 0.15s;

					&:hover {
						box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
						transform: translateY(-2px);
					}

					.name {
						font-size: 12px;
						font-weight: 500;
						color: #1a1a1a;
						margin-bottom: 6px;
						line-height: 1.4;
						display: -webkit-box;
						-webkit-line-clamp: 2;
						-webkit-box-orient: vertical;
						overflow: hidden;
					}

					.price {
						font-size: 15px;
						font-weight: 600;
						color: #f5222d;

						span {
							font-size: 11px;
							font-weight: 400;
						}
					}

					.tag {
						font-size: 11px;
						color: #999;
						margin-top: 4px;
					}
				}
			}
		}

		.copy {
			display: flex;
			align-items: flex-end;
			padding-bottom: 4px;

			a {
				color: #bbb;
				cursor: pointer;
				font-size: 14px;

				&:hover {
					color: #666;
				}
			}
		}
	}
}

/* 预览弹窗全图 */
.preview-full-img {
	max-width: 80vw;
	max-height: 80vh;
	display: block;
}

@keyframes blink {
	50% { opacity: 0; }
}

@keyframes spin {
	to { transform: rotate(360deg); }
}
</style>
